<template>
  <b-form id="checkout-view" v-if="registrationData">
    <b-container fluid>
      <b-row id="header">
        <b-col class="text-center">
          <img src="@/assets/images/rush-grayscale.png" alt="Rush" />
        </b-col>
      </b-row>
    </b-container>
    <b-container fluid>
      <b-row id="contents">
        <b-col lg="6" id="payment-and-billing-details">
          <b-container>
            <b-row>
              <b-col lg="10">
                <div class="section-title">Credit Card Details</div>
                <small class="text-secondary">Your transactions are secured.</small>
                <b-form-group class="mt-2" label="Card Number" label-for="card-number"
                  :state="validateState('cardNumber')" :invalid-feedback="getErrorMessage('cardNumber')">
                  <input v-model="form.cardNumber" type="text" id="card-number" class="form-control" inputmode="numeric"
                    pattern="[0-9]*" placeholder="Enter Card Number" autocomplete="off" autofocus />
                  <img v-if="cardBrand && validateState('cardNumber')"
                    :src="require(`@/assets/images/${cardBrand}.png`)" class="card-brand" :class="cardBrand"
                    alt="Card Brand" />
                </b-form-group>
                <b-row>
                  <b-col md="6">
                    <b-form-group label="Expiry Date" label-for="expiration-date" :state="validateState('expiryDate')"
                      :invalid-feedback="getErrorMessage('expiryDate')">
                      <input v-model="form.expiryDate" type="text" id="expiration-date" class="form-control"
                        placeholder="MM/YY" autocomplete="off" @keyup="formatMonthAndYear" @keydown="formatMonthAndYear"
                        maxlength="5" />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="CVV" label-for="cvv" :state="validateState('cvv')"
                      :invalid-feedback="getErrorMessage('cvv')">
                      <input v-model="form.cvv" type="password" id="cvv" class="form-control" placeholder="Enter CVV"
                        inputmode="numeric" pattern="[0-9]*" autocomplete="new-password" />
                    </b-form-group>
                  </b-col>
                </b-row>
                <small>
                  <p class="lh-normal mt-3">
                    <b>Note:</b> We are currently unable to process payments from BDO debit and credit cards. In the
                    meantime, we encourage you to use a different credit or debit card provider to ensure hassle-free
                    payments.
                  </p>
                </small>
                <div class="section-title mt-5">Billing Details</div>
                <b-row class="mt-3">
                  <b-col md="6">
                    <b-form-group label="First Name" label-for="first-name" :state="validateState('firstName')"
                      :invalid-feedback="getErrorMessage('firstName')">
                      <input v-model="form.firstName" type="text" id="first-name" class="form-control"
                        placeholder="Enter First Name" autocomplete="off" maxlength="255" />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Last Name" label-for="last-name" :state="validateState('lastName')"
                      :invalid-feedback="getErrorMessage('lastName')">
                      <input v-model="form.lastName" type="text" id="last-name" class="form-control"
                        placeholder="Enter Last Name" autocomplete="off" maxlength="255" />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="6">
                    <b-form-group label="Mobile Number" label-for="mobile-number" :state="validateState('mobileNumber')"
                      :invalid-feedback="getErrorMessage('mobileNumber')">
                      <b-input-group id="mobile-number-input-group">
                        <template #prepend>
                          <div class="border rounded-left">
                            <b-dropdown id="mobile-country-code-dropdown" class="base-dropdown bg-white border-0"
                              variant="link" toggle-class="text-decoration-none w-100 rounded-0 border-0"
                              menu-class="w-100" no-caret disabled>
                              <template #button-content>
                                <div class="d-flex justify-content-between align-items-center">
                                  <img src="@/assets/images/flag-ph-rounded.png" alt="PH" />
                                  <div>
                                    {{ mobileCountryCode }}
                                  </div>
                                  <img src="@/assets/images/chevron-down.png" class="base-dropdown-icon"
                                    alt="Dropdown" />
                                </div>
                              </template>
                            </b-dropdown>
                          </div>
                        </template>
                        <input v-model="form.mobileNumber" type="text" id="mobile-number" class="form-control"
                          placeholder="Enter Mobile Number" autocomplete="off" inputmode="numeric" pattern="[0-9]*" />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Email Address" label-for="email-address" :state="validateState('emailAddress')"
                      :invalid-feedback="getErrorMessage('emailAddress')">
                      <input v-model="form.emailAddress" type="email" id="email-address" class="form-control"
                        placeholder="Enter Email Address" autocomplete="off" maxlength="50" />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-form-group label="Address Line (Village or Bldg. Name and Apt Unit No., Street Address)"
                  label-for="address-line" :state="validateState('addressLine')"
                  :invalid-feedback="getErrorMessage('addressLine')">
                  <input v-model="form.addressLine" type="text" id="address-line" class="form-control" :style="[
    isMobileScreen ? { height: '75px' } : { height: '44px' },
  ]" placeholder="Enter Address Line" autocomplete="off" maxlength="255" />
                </b-form-group>
                <b-form-group label="Country" label-for="country-dropdown">
                  <b-dropdown id="country-dropdown" class="base-dropdown" variant="link"
                    toggle-class="text-decoration-none w-100" menu-class="w-100" no-caret disabled>
                    <template #button-content>
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          {{ form.country.name }}
                        </div>
                        <img src="@/assets/images/chevron-down.png" class="base-dropdown-icon" alt="Dropdown" />
                      </div>
                    </template>
                  </b-dropdown>
                </b-form-group>
                <b-form-group label="Province" label-for="province-dropdown" :state="validateState('province')"
                  :invalid-feedback="getErrorMessage('province')">
                  <b-dropdown id="province-dropdown" class="base-dropdown" variant="link"
                    toggle-class="text-decoration-none w-100" menu-class="w-100" no-caret>
                    <template #button-content>
                      <div class="d-flex justify-content-between align-items-center">
                        <div> {{ form.province ? form.province : "Select" }}</div>
                        <img src="@/assets/images/chevron-down.png" class="base-dropdown-icon" alt="Dropdown" />
                      </div>
                    </template>
                    <template #default>
                      <div class="base-dropdown-menu">
                        <b-dropdown-item v-for="(province, index) in provinces" :key="index"
                          @click="selectProvince(province)">
                          {{ province.name }}
                        </b-dropdown-item>
                      </div>
                    </template>
                  </b-dropdown>
                </b-form-group>
                <b-row>
                  <b-col md="6">
                    <b-form-group label="City" label-for="city" :state="validateState('city')"
                      :invalid-feedback="getErrorMessage('city')">
                      <b-dropdown id="city-dropdown" class="base-dropdown" variant="link"
                        toggle-class="text-decoration-none w-100" menu-class="w-100" no-caret
                        :disabled="!form.province">
                        <template #button-content>
                          <div class="d-flex justify-content-between align-items-center">
                            <div>{{ form.city ? form.city : "Select" }}</div>
                            <img src="@/assets/images/chevron-down.png" class="base-dropdown-icon" alt="Dropdown" />
                          </div>
                        </template>
                        <template #default>
                          <div class="base-dropdown-menu">
                            <b-dropdown-item v-for="(city, index) in cities" :key="index" @click="selectCity(city)">
                              <div>
                                {{ city.name }}
                              </div>
                            </b-dropdown-item>
                          </div>
                        </template>
                      </b-dropdown>
                      <b-popover v-if="!form.province" target="city-dropdown" triggers="hover" placement="top">
                        Please select a province first.
                      </b-popover>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Postal Code" label-for="postal-code" :state="validateState('postalCode')"
                      :invalid-feedback="getErrorMessage('postalCode')">
                      <input v-model="form.postalCode" type="text" id="postal-code" class="form-control"
                        placeholder="1234" autocomplete="off" inputmode="numeric" pattern="[0-9]*" />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-container>
        </b-col>
        <b-col lg="6" id="review-your-plan" v-if="selectedPlan">
          <b-container>
            <b-row>
              <b-col lg="10">
                <div class="section-title">Review your plan</div>
                <div class="mt-4 text-dark font-weight-bold pl-0">
                  Pay your plan
                </div>
                <b-tabs v-model="billingRecurrenceTabIndex" class="mt-2" pills>
                  <b-tab v-for="(billingRecurrence, index) in billingRecurrences" :key="index"
                    :title="billingRecurrence.title" :title-link-class="getBillingRecurrenceTabClasses(index)">
                    <b-container>
                      <b-row class="mt-4 py-3 border-top border-bottom">
                        <b-col md="3" class="plan-image-container">
                          <img v-if="selectedPlan.logo" :src="selectedPlan.logo" alt="Plan" class="plan-image" />
                        </b-col>
                        <b-col md="4" class="py-1 text-center text-md-left mt-2 mt-lg-0">
                          <div class="h4">{{ selectedPlan.name }}</div>
                          <div class="plan-per-month" v-if="selectedBillingRecurrence">
                            &#8369;{{ multipliedAmount | currency }}/{{
    selectedBillingRecurrence.unit
  }}
                          </div>
                        </b-col>
                        <b-col md="5" class="py-1 px-0 text-center text-md-right font-weight-bold">
                          <span id="save-discount" class="green-pill" v-if="selectedBillingRecurrenceBreakdown &&
    selectedBillingRecurrenceBreakdown.discount_value
    ">
                            Save
                            {{
    selectedBillingRecurrenceBreakdown.discount_value
  }}
                          </span>
                          <span class="fs-14">
                            &#8369;{{ multipliedAmount | currency }}
                          </span>
                        </b-col>
                      </b-row>
                    </b-container>
                  </b-tab>
                </b-tabs>
                <b-row class="border-bottom py-3" v-if="!referralCodeApplied &&
    form.billingRecurrence === 'monthly' &&
    !selectedPlan.is_free
    ">
                  <b-col md="9" class="">
                    <div class="form-floating">
                      <b-input v-model="form.promoCode" type="text" id="promo-code" class="form-control"
                        :class="promoCodeApplied ? ' is-valid' : ''" placeholder="Enter Promo Code" autocomplete="off"
                        :disabled="promoCodeApplied" @keypress.native="filterAlphaNumeric" @paste.prevent />
                      <label for="promo-code">Promo Code</label>
                      <b-form-invalid-feedback :force-show="promoCodeError != ''" class="mt-2">
                        {{ promoCodeError }}
                      </b-form-invalid-feedback>
                    </div>
                  </b-col>
                  <b-col md="3" v-show="!promoCodeApplied">
                    <b-btn variant="primary" block size="lg" @click="applyPromoCode" :disabled="!form.promoCode"
                      class="mt-3 mt-md-0">
                      Apply
                    </b-btn>
                  </b-col>
                  <b-col md="3" v-show="promoCodeApplied">
                    <b-btn variant="dark" block size="lg" @click="removePromoCode" class="mt-3 mt-md-0">
                      Remove
                    </b-btn>
                  </b-col>
                </b-row>
                <div class="d-flex justify-content-between mt-4">
                  <div class="fs-14">Subtotal</div>
                  <div class="fs-14 font-weight-bold">
                    &#8369;{{ multipliedAmount | currency }}
                  </div>
                </div>
                <template v-if="selectedBillingRecurrenceBreakdown">
                  <div class="d-flex justify-content-between mt-2">
                    <div class="fs-14">
                      VAT ({{ selectedBillingRecurrenceBreakdown.vat_value }})
                    </div>
                    <div class="fs-14 font-weight-bold" v-if="promoCodeApplied || referralCodeApplied">
                      &#8369;{{ vatWithPromo }}
                    </div>
                    <div class="fs-14 font-weight-bold" v-else>
                      &#8369;{{
    selectedBillingRecurrenceBreakdown.vat_amount | currency
  }}
                    </div>
                  </div>
                  <div class="d-flex justify-content-between mt-2 text-success" v-if="selectedBillingRecurrenceBreakdown.discount_value &&
    selectedBillingRecurrenceBreakdown.discount_amount
    ">
                    <div class="fs-14">
                      <span class="font-weight-bold">Discount</span>
                      <b-icon icon="tag-fill" class="ml-1" />
                      <div class="discount-details">
                        {{ selectedBillingRecurrenceBreakdown.discount_value }}
                        off for
                        {{
    discountSubscriptionLabel[
    selectedBillingRecurrenceBreakdown
      .billing_recurrence
    ]
  }}
                        subscription
                      </div>
                    </div>
                    <div class="fs-14 font-weight-bold">
                      - &#8369;{{
      selectedBillingRecurrenceBreakdown.discount_amount
      | currency
    }}
                    </div>
                  </div>
                  <template v-if="referralCodeApplied &&
    form.billingRecurrence === 'monthly' &&
    promoCodeAmount
    ">
                    <div class="d-flex justify-content-between text-success mt-2">
                      <div class="fs-14 font-weight-bold">
                        Discount <b-icon icon="tag-fill" class="ml-1" />
                      </div>
                      <div class="fs-14 font-weight-bold">
                        - &#8369;{{ promoCodeAmount }}
                      </div>
                    </div>
                    <div class="d-flex justify-content-between">
                      <div class="fs-14 discount-details">
                        {{ promoDescription }}
                      </div>
                    </div>
                  </template>
                  <div class="d-flex justify-content-between text-success mt-2"
                    v-if="promoCodeApplied && promoCodeAmount">
                    <div class="fs-14 font-weight-bold">
                      Promo Code <b-icon icon="tag-fill" class="ml-1" />
                    </div>
                    <div class="fs-14 font-weight-bold">
                      - &#8369;{{ promoCodeAmount }}
                    </div>
                  </div>
                  <div class="d-flex justify-content-between" v-if="promoCodeApplied">
                    <div class="fs-14 discount-details">
                      {{ promoDescription }}
                    </div>
                  </div>
                  <div class="d-flex justify-content-between my-4 border-top pt-4">
                    <div class="h5">To Pay</div>
                    <div class="text-right">
                      <div class="h5 font-weight-bold" v-if="promoCodeApplied || referralCodeApplied">
                        &#8369;{{ promoToPay }}
                      </div>
                      <div v-else class="h5 font-weight-bold">
                        &#8369;{{
    selectedBillingRecurrenceBreakdown.charge_total_amount
    | currency
  }}
                      </div>
                      <div class="green-pill" v-if="selectedPlan.is_free &&
    selectedPlan.range_type_free !== 'unlimited'
    ">
                        Free Trial for {{ selectedPlan.range_free }}
                        {{
    selectedPlan.range_free === 1
      ? selectedPlan.range_type_free.slice(0, -1)
                        : selectedPlan.range_type_free
                        }}
                      </div>
                    </div>
                  </div>
                </template>
                <b-alert :show="promoCodeApplied" variant="warning">
                  <b>IMPORTANT NOTICE:</b> The discounted subscription fee will
                  be charged on this payment method throughout the period
                  covered by the promo code. After the validity period of the
                  promo code has ended, the succeeding recurring charges will
                  revert to the regular subscription fee.
                </b-alert>
                <b-overlay :show="loading">
                  <b-btn class="mt-3" variant="primary" size="lg" :disabled="loading ||
    $v.form.$invalid ||
    !selectedBillingRecurrenceBreakdown
    " block pill @click="checkout">
                    Checkout
                  </b-btn>
                </b-overlay>
              </b-col>
            </b-row>
          </b-container>
        </b-col>
      </b-row>
    </b-container>
    <b-modal v-model="isOpenAuthenticationModal" title="Card Authentication" @hidden="resetAuthenticationModal"
      dialog-class="modal-full-dialog" content-class="modal-full-content" centered hide-footer>
      <template v-if="authenticationUrl">
        <iframe title="Card Authentication" :src="authenticationUrl" class="h-100 w-100 border-0"></iframe>
      </template>
    </b-modal>
  </b-form>
</template>

<script>
import { provinces, cities } from "philippines";
import _ from "lodash";
import { mapGetters, mapState, mapActions } from "vuex";
import { required, email, helpers } from "vuelidate/lib/validators";
const cardNumber = (value) => !helpers.req(value) || window.Xendit.card.validateCardNumber(value);
const expiryDate = (value) => !helpers.req(value) || window.Xendit.card.validateExpiry(
  value.split('/')[0],
  2000 + parseInt(value.split('/').pop())
);
const cvv = (value) => !helpers.req(value) || window.Xendit.card.validateCvn(value);
const alphaNumericSpace = (value) => !helpers.req(value) || /^[\w ]*$/.test(value);
const mobileNumber = (value) => !helpers.req(value) || (value.startsWith('9') && value.length === 10);

export default {
  data() {
    return {
      maxMobileWidth: 767,
      isMobileScreen: false,
      cardBrand: "",
      billingRecurrenceTabIndex: null,
      form: {
        cardNumber: null,
        expiryDate: null,
        cvv: null,
        firstName: null,
        lastName: null,
        country: {
          code: "PH",
          name: "Philippines",
          currency: "PHP",
        },
        addressLine: null,
        postalCode: null,
        province: null,
        city: null,
        emailAddress: null,
        mobileNumber: "",
        planUuid: null,
        billingRecurrence: null,
        promoCode: null,
      },
      mobileCountryCode: "+63",
      provinces: _.sortBy(provinces, ["name"]),
      cities: _.sortBy(cities, ["name"]),
      xenditCard: window.Xendit.card,
      billingRecurrences: [
        {
          title: "monthly",
          name: "monthly",
          multiplier: 1,
          breakdown: "monthly_breakdown",
          unit: "month",
        },
        {
          title: "quarterly",
          name: "quarterly",
          multiplier: 3,
          breakdown: "quarterly_breakdown",
          unit: "quarter",
        },
        {
          title: "semiannually",
          name: "bi-annual",
          multiplier: 6,
          breakdown: "bi_annual_breakdown",
          unit: "6 months",
        },
        {
          title: "annually",
          name: "yearly",
          multiplier: 12,
          breakdown: "annual_breakdown",
          unit: "year",
        },
      ],
      authenticationUrl: null,
      isOpenAuthenticationModal: false,
      promoCodeApplied: false,
      promoAmount: 0,
      promoCodeError: "",
      promoDescription: "",
      promoVat: 0,
      toPay: 0,
      discountSubscriptionLabel: {
        monthly: "monthly",
        quarterly: "quarterly",
        bi_annual: "semi-annual",
        yearly: "annual",
      },
      referralCodeApplied: false,
    };
  },
  validations: {
    form: {
      cardNumber: {
        required,
        cardNumber
      },
      expiryDate: {
        required,
        expiryDate
      },
      cvv: {
        required,
        cvv
      },
      firstName: {
        required,
        alphaNumericSpace
      },
      lastName: {
        required,
        alphaNumericSpace
      },
      mobileNumber: {
        required,
        mobileNumber
      },
      emailAddress: {
        required,
        email
      },
      addressLine: {
        required
      },
      province: {
        required
      },
      city: {
        required
      },
      postalCode: {
        required
      }
    }
  },
  computed: {
    ...mapState("auth", ["registrationData", "planList", "loading"]),
    ...mapGetters("auth", ["authErrors"]),
    ...mapGetters("promotional", ["promoErrors", "promoCodeDetails"]),
    ...mapActions({
      verifyPromoCode: "promotional/verifyPromoCode",
    }),
    selectedPlan() {
      return this.planList.length
        ? this.planList.find(
          (plan) => plan.uuid === this.registrationData.plan_uuid
        )
        : null;
    },
    selectedBillingRecurrence() {
      const selectedBillingRecurrence =
        this.billingRecurrences[this.billingRecurrenceTabIndex];

      return selectedBillingRecurrence || null;
    },
    selectedBillingRecurrenceBreakdown() {
      return this.selectedBillingRecurrence
        ? this.selectedPlan[this.selectedBillingRecurrence.breakdown]
        : null;
    },
    multipliedAmount() {
      return this.selectedPlan && this.selectedBillingRecurrence
        ? this.selectedPlan.price * this.selectedBillingRecurrence.multiplier
        : 0;
    },
    promoCodeAmount() {
      return this.promoAmount
        ? Number(parseFloat(this.promoAmount).toFixed(2)).toLocaleString("en", {
          minimumFractionDigits: 2,
        })
        : "0";
    },
    vatWithPromo() {
      return Number(parseFloat(this.promoVat).toFixed(2)).toLocaleString("en", {
        minimumFractionDigits: 2,
      });
    },
    promoToPay() {
      return Number(parseFloat(this.toPay).toFixed(2)).toLocaleString("en", {
        minimumFractionDigits: 2,
      });
    },
  },
  watch: {
    selectedBillingRecurrence(value) {
      if (value) {
        this.form.billingRecurrence = value.name;
      }
    },
    "form.expiryDate"(value) {
      if (!this.$v.form.expiryDate.$dirty)
        this.$v.form.expiryDate.$touch();

      this.form.expiryDate = value.replace(/[^0-9/]/g, "");
    },
    "form.cardNumber"(value) {
      if (!this.$v.form.cardNumber.$dirty)
        this.$v.form.cardNumber.$touch();

      this.form.cardNumber = value.replace(/\D/g, "").slice(0, 16);
      this.cardBrand = this.getCardBrand(this.form.cardNumber);
    },
    "form.cvv"(value) {
      if (!this.$v.form.cvv.$dirty)
        this.$v.form.cvv.$touch();

      this.form.cvv = value.replace(/\D/g, "").slice(0, 3);
    },
    "form.firstName"() {
      if (!this.$v.form.firstName.$dirty)
        this.$v.form.firstName.$touch();
    },
    "form.lastName"() {
      if (!this.$v.form.lastName.$dirty)
        this.$v.form.lastName.$touch();
    },
    "form.postalCode"(value) {
      if (!this.$v.form.postalCode.$dirty)
        this.$v.form.postalCode.$touch();

      this.form.postalCode = value.replace(/\D/g, "").slice(0, 4);
    },
    "form.emailAddress"() {
      if (!this.$v.form.emailAddress.$dirty)
        this.$v.form.emailAddress.$touch();
    },
    "form.addressLine"() {
      if (!this.$v.form.addressLine.$dirty)
        this.$v.form.addressLine.$touch();
    },
    "form.province"() {
      if (!this.$v.form.province.$dirty)
        this.$v.form.province.$touch();
    },
    "form.city"() {
      if (!this.$v.form.city.$dirty)
        this.$v.form.city.$touch();
    },
    "form.mobileNumber"(value) {
      if (!this.$v.form.mobileNumber.$dirty)
        this.$v.form.mobileNumber.$touch();

      this.form.mobileNumber = value.replace(/\D/g, "").slice(0, 10);
    },
    "form.promoCode"(value) {
      this.form.promoCode = value.trim();
      if (this.form.promoCode == "") this.promoCodeError = "";
    },
    multipliedAmount() {
      if (this.promoCodeApplied || this.referralCodeApplied) {
        let details = this.promoCodeDetails.promo_details;
        this.promoAmount =
          this.multipliedAmount * (details.discount_value / 100);

        let recurrence = this.selectedBillingRecurrence.breakdown;
        if (recurrence == "monthly_breakdown") {
          this.promoAmount =
            this.promoCodeDetails.monthly_breakdown.promo_code_amount;
          this.promoVat = this.promoCodeDetails.monthly_breakdown.vat_amount;
          this.toPay =
            this.promoCodeDetails.monthly_breakdown.charge_total_amount;
        } else if (recurrence == "quarterly_breakdown") {
          this.promoAmount =
            this.promoCodeDetails.quarterly_breakdown.promo_code_amount;
          this.promoVat = this.promoCodeDetails.quarterly_breakdown.vat_amount;
          this.toPay =
            this.promoCodeDetails.quarterly_breakdown.charge_total_amount;
        } else if (recurrence == "bi_annual_breakdown") {
          this.promoAmount =
            this.promoCodeDetails.bi_annual_breakdown.promo_code_amount;
          this.promoVat = this.promoCodeDetails.bi_annual_breakdown.vat_amount;
          this.toPay =
            this.promoCodeDetails.bi_annual_breakdown.charge_total_amount;
        } else {
          this.promoAmount =
            this.promoCodeDetails.annual_breakdown.promo_code_amount;
          this.promoVat = this.promoCodeDetails.annual_breakdown.vat_amount;
          this.toPay =
            this.promoCodeDetails.annual_breakdown.charge_total_amount;
        }
      }
    },
  },
  methods: {
    checkIfMobileScreen() {
      this.isMobileScreen = window.innerWidth < this.maxMobileWidth;
    },
    validateState(key) {
      const { $dirty, $error } = this.$v.form[key];

      return $dirty ? !$error : null;
    },
    getErrorMessage(key) {
      const field = this.$v.form[key];

      if ("required" in field && !field.required)
        return `${_.capitalize(_.startCase(key))} is required.`;

      if ("cardNumber" in field && !field.cardNumber)
        return "Please enter a valid credit card number.";

      if ("expiryDate" in field && !field.expiryDate)
        return "Please enter a valid expiration date.";

      if ("cvv" in field && !field.cvv)
        return "Please enter a valid cvv.";

      if ("alphaNumericSpace" in field && !field.alphaNumericSpace)
        return `${_.capitalize(_.startCase(key))} may only contain letters and spaces.`;

      if ("email" in field && !field.email)
        return "Please enter a valid email address.";

      if ("mobileNumber" in field && !field.mobileNumber)
        return "Please enter a valid mobile number.";

      return "";
    },
    formatMonthAndYear(event) {
      const backspaceKeyCode = 8;

      if (event.keyCode === backspaceKeyCode) return;

      event.target.value = event.target.value
        .replace(/^([1-9]\/|[2-9])$/g, "0$1/")
        .replace(/^(0[1-9]|1[0-2])$/g, "$1/")
        .replace(/^([0-1])([3-9])$/g, "0$1/$2")
        .replace(/^(0?[1-9]|1[0-2])(\d{2})$/g, "$1/$2")
        .replace(/^([0]+)\/|[0]+$/g, "0")
        .replace(/[^\d/]|^[/]*$/g, "")
        .replace(/\/\//g, "/");
    },
    getBillingRecurrenceTabClasses(index) {
      if (this.billingRecurrenceTabIndex === index)
        return ["py-1", "text-capitalize", "rounded-pill"];
      else return ["py-1", "text-capitalize", "text-dark"];
    },
    selectProvince(province) {
      this.form.province = province.name;
      this.cities = cities.filter((city) => city.province === province.key);
    },
    selectCity(city) {
      this.form.city = city.name;
    },
    getCardBrand(cardNumber) {
      const visa = /^4\d{12}(?:\d{3})?$/;
      const mastercard =
        /^(?:5[1-5]\d{2}|222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)\d{12}$/;
      const jcb = /^(3(?:088|096|112|158|337|5(?:2[89]|[3-8]\d))\d{12})$/;

      if (visa.test(cardNumber)) return "visa";

      if (mastercard.test(cardNumber)) return "mastercard";

      if (jcb.test(cardNumber)) return "jcb";

      return "";
    },
    checkout() {
      if (!this.$v.form.$dirty)
        this.$v.form.$touch();

      if (
        !this.loading &&
        !this.$v.form.$invalid &&
        this.selectedBillingRecurrenceBreakdown
      ) {
        const [month, year] = this.form.expiryDate.split("/");

        this.$store.commit("auth/loading", true);
        this.xenditCard.createToken(
          {
            amount: parseFloat(
              this.selectedBillingRecurrenceBreakdown.charge_total_amount
            ).toFixed(2),
            card_number: this.form.cardNumber,
            card_exp_month: month,
            card_exp_year: (2000 + parseInt(year)).toString(),
            card_cvn: this.form.cvv,
            is_multiple_use: true,
            should_authenticate: false,
            billing_details: {
              address: {
                country: this.form.country.code,
                street_line1: this.form.addressLine,
                city: this.form.city,
                province_state: this.form.province,
                postal_code: this.form.postalCode,
              },
            },
            card_holder_email: this.form.emailAddress,
            card_holder_first_name: this.form.firstName,
            card_holder_last_name: this.form.lastName,
            card_holder_phone_number: this.mobileCountryCode + this.form.mobileNumber
          },
          (cardTokenError, cardTokenReponse) => {
            if (cardTokenError) {
              this.$store.commit("auth/loading", false);
              this.$root.$emit("showPromptModal", {
                message: cardTokenError.message,
              });

              return;
            }

            if (cardTokenReponse.id) {
              this.xenditCard.createAuthentication(
                {
                  amount: parseFloat(
                    this.selectedBillingRecurrenceBreakdown.charge_total_amount
                  ).toFixed(2),
                  token_id: cardTokenReponse.id,
                  currency: this.form.country.currency,
                  card_data: {
                    card_holder_email: this.form.emailAddress,
                    card_holder_first_name: this.form.firstName,
                    card_holder_last_name: this.form.lastName,
                    card_holder_phone_number: this.mobileCountryCode + this.form.mobileNumber
                  }
                },
                async (cardAuthError, cardAuthResponse) => {
                  if (cardAuthError) {
                    this.$store.commit("auth/loading", false);
                    this.$root.$emit("showPromptModal", {
                      message: cardAuthError.message,
                    });

                    return;
                  }

                  switch (cardAuthResponse.status) {
                    case "VERIFIED": {
                      const payload = {
                        ..._.omit(this.registrationData, ["referral_code"]),
                        billing_address: [
                          this.form.addressLine,
                          this.form.province,
                          this.form.city,
                          this.form.country.name,
                        ].join(", "),
                        billing_recurrence: this.form.billingRecurrence,
                        charge_amount: parseFloat(
                          this.selectedBillingRecurrenceBreakdown
                            .charge_total_amount
                        ).toFixed(2),
                        charge_amount_total_with_promo: parseFloat(
                          this.promoToPay
                        ).toFixed(2),
                        tokenization_data: JSON.stringify({
                          token: cardTokenReponse.id,
                          response: {
                            token_response: cardTokenReponse,
                            authentication_response: cardAuthResponse,
                            masked_card_number:
                              cardTokenReponse.masked_card_number,
                            ...(cardAuthResponse.id && {
                              authentication_id: cardAuthResponse.id,
                            }),
                          },
                        }),
                      };

                      if (
                        this.promoCodeDetails &&
                        this.promoCodeDetails.promo_details
                      ) {
                        const { promo_type, uuid } =
                          this.promoCodeDetails.promo_details;

                        if (promo_type === "discount_code")
                          payload["promo_code_uuid"] = uuid;

                        if (
                          promo_type === "referral_code" &&
                          this.form.billingRecurrence === "monthly"
                        )
                          payload["referral_code_uuid"] = uuid;
                      }

                      await this.$store.dispatch("auth/registerUser", payload);

                      if (this.isOpenAuthenticationModal)
                        this.isOpenAuthenticationModal = false;

                      if (_.isEmpty(this.authErrors))
                        this.$router.replace({ name: "Success" });

                      break;
                    }
                    case "IN_REVIEW":
                      this.authenticationUrl =
                        cardAuthResponse.payer_authentication_url;
                      this.isOpenAuthenticationModal = true;
                      break;
                    case "FAILED":
                      this.$store.commit("auth/loading", false);
                      this.$root.$emit("showPromptModal", {
                        message: `${_.capitalize(
                          _.lowerCase(cardAuthResponse.failure_reason)
                        )}.`,
                      });
                      break;
                    default:
                      this.$store.commit("auth/loading", false);
                      this.$root.$emit("showPromptModal", {
                        message: "Something went wrong.",
                      });
                  }
                }
              );
            } else {
              this.$store.commit("auth/loading", false);
              this.$root.$emit("showPromptModal", {
                message: "Something went wrong.",
              });
            }
          }
        );
        window.fbq("trackCustom", "Click Checkout Button");
      }
    },
    resetAuthenticationModal() {
      this.authenticationUrl = null;
      this.$store.commit("auth/loading", false);
    },
    async applyPromoCode() {
      if (this.form.promoCode != "" && this.form.promoCode != null) {
        let payload = {
          code: this.form.promoCode,
          plan_uuid: this.form.planUuid,
          type: "discount_code",
        };
        await this.$store.dispatch("promotional/verifyPromoCode", payload);

        if (this.promoErrors != null) {
          this.promoCodeError =
            "That code you entered is invalid or no longer available.";
        } else {
          this.promoCodeApplied = true;
          this.promoCodeError = "";

          let details = this.promoCodeDetails.promo_details;
          let recurrence = this.selectedBillingRecurrence.breakdown;

          if (recurrence == "monthly_breakdown") {
            this.promoAmount =
              this.promoCodeDetails.monthly_breakdown.promo_code_amount;
            this.promoVat = this.promoCodeDetails.monthly_breakdown.vat_amount;
            this.toPay =
              this.promoCodeDetails.monthly_breakdown.charge_total_amount;
          } else if (recurrence == "quarterly_breakdown") {
            this.promoAmount =
              this.promoCodeDetails.quarterly_breakdown.promo_code_amount;
            this.promoVat =
              this.promoCodeDetails.quarterly_breakdown.vat_amount;
            this.toPay =
              this.promoCodeDetails.quarterly_breakdown.charge_total_amount;
          } else if (recurrence == "bi_annual_breakdown") {
            this.promoAmount =
              this.promoCodeDetails.bi_annual_breakdown.promo_code_amount;
            this.promoVat =
              this.promoCodeDetails.bi_annual_breakdown.vat_amount;
            this.toPay =
              this.promoCodeDetails.bi_annual_breakdown.charge_total_amount;
          } else {
            this.promoAmount =
              this.promoCodeDetails.annual_breakdown.promo_code_amount;
            this.promoVat = this.promoCodeDetails.annual_breakdown.vat_amount;
            this.toPay =
              this.promoCodeDetails.annual_breakdown.charge_total_amount;
          }

          this.promoDescription =
            details.code + "(" + details.description + ")";
        }
      }
    },
    async applyReferralCode() {
      const details = this.promoCodeDetails.promo_details;
      const recurrence = this.selectedBillingRecurrence.breakdown;

      if (recurrence == "monthly_breakdown") {
        this.promoAmount =
          this.promoCodeDetails.monthly_breakdown.promo_code_amount;
        this.promoVat = this.promoCodeDetails.monthly_breakdown.vat_amount;
        this.toPay =
          this.promoCodeDetails.monthly_breakdown.charge_total_amount;
      } else if (recurrence == "quarterly_breakdown") {
        this.promoAmount =
          this.promoCodeDetails.quarterly_breakdown.promo_code_amount;
        this.promoVat = this.promoCodeDetails.quarterly_breakdown.vat_amount;
        this.toPay =
          this.promoCodeDetails.quarterly_breakdown.charge_total_amount;
      } else if (recurrence == "bi_annual_breakdown") {
        this.promoAmount =
          this.promoCodeDetails.bi_annual_breakdown.promo_code_amount;
        this.promoVat = this.promoCodeDetails.bi_annual_breakdown.vat_amount;
        this.toPay =
          this.promoCodeDetails.bi_annual_breakdown.charge_total_amount;
      } else {
        this.promoAmount =
          this.promoCodeDetails.annual_breakdown.promo_code_amount;
        this.promoVat = this.promoCodeDetails.annual_breakdown.vat_amount;
        this.toPay = this.promoCodeDetails.annual_breakdown.charge_total_amount;
      }

      this.referralCodeApplied = true;
      this.promoDescription = `${details.code}(${details.description})`;
    },
    removePromoCode() {
      this.promoCodeApplied = false;
      this.form.promoCode = "";
      this.promoAmount = 0;
    },
    filterAlphaNumeric(e) {
      if (/[^A-Za-z0-9]/g.test(String.fromCharCode(e.keyCode)))
        e.preventDefault();
    },
  },
  filters: {
    currency(value) {
      if (!value) return "0";

      return Number(parseFloat(value).toFixed(2)).toLocaleString("en", {
        minimumFractionDigits: 2,
      });
    },
  },
  mounted() {
    window.addEventListener("resize", this.checkIfMobileScreen);
  },
  created() {
    this.isMobileScreen = window.innerWidth < this.maxMobileWidth;

    if (!this.registrationData) this.$router.push({ name: "Home" });
    else {
      this.form.firstName = this.registrationData.first_name;
      this.form.lastName = this.registrationData.last_name;
      this.form.emailAddress = this.registrationData.email;
      this.form.mobileNumber = this.registrationData.mobile_number;
      this.form.planUuid = this.selectedPlan.uuid;
    }

    if (
      _.get(this.promoCodeDetails, "promo_details.promo_type", null) ===
      "referral_code"
    ) {
      this.$nextTick(() => {
        this.applyReferralCode();
      });
    }
  },
  destroyed() {
    window.removeEventListener("resize", this.checkIfMobileScreen);
    this.$store.commit("promotional/promoCodeDetails", null);
  },
};
</script>

<style lang="scss">
#checkout-view {
  background-color: #fafafa;
  font-family: SKModernist, sans-serif;


  #header {
    border-bottom: 1px solid #dedcde;
    padding-top: 28px;
    padding-bottom: 26px;
  }

  #contents {
    padding-bottom: 80px;

    #payment-and-billing-details,
    #review-your-plan {
      padding: 60px 30px;
    }
  }

  #payment-and-billing-details {
    background-color: #fffefe;
  }

  #review-your-plan {
    background-color: #fafafa;
  }

  .section-title {
    font-size: 20px;
  }

  .card-brand {
    width: 30px;
    position: absolute;
    right: 13px;
  }

  .visa {
    top: 18px;
  }

  .mastercard {
    top: 12px;
  }

  .jcb {
    top: 10px;
  }

  .plan-image-container {
    padding-top: 1rem;
    padding-bottom: 1rem;
    border: 1px solid #e9ebed;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .plan-image {
    width: auto;
    height: 30px;
  }

  .plan-per-month {
    font-size: 14px;
  }

  .form-group {
    flex: 1;
    margin-bottom: 1.5rem;

    input {
      height: 44px;
    }
  }

  #mobile-country-code-dropdown {
    height: 42px;
    width: 87px;
  }

  .btn-primary.disabled,
  .btn-primary:disabled {
    opacity: 1;
    background: #F8F9F9;
    border-color: #e9ebed;
    color: #D8DCDE;
  }

  .fs-14 {
    font-size: 14px;
  }

  .discount-details {
    color: #68737d;
    font-size: 14px;
    word-break: break-word;
  }

  .green-pill {
    padding: 5px;
    color: #20b866;
    font-size: 14px;
    font-family: 'SKModernistBold', sans-serif;
    background-color: rgba(32, 184, 102, 0.1);
    border-radius: 5px;
  }

  #save-discount {
    margin-right: 8px;
  }

  .btn-dark {
    background-color: #343a40;
    border-color: #343a40;
    font-family: SKModernist, sans-serif;
    font-size: 16px;
    border-radius: 5px;
  }

  .form-control.is-valid {
    border-color: #D8DCDE;
    background-image: url("../assets/images/green-check.svg");
  }

  label {
    font-size: 14px;
    margin-bottom: 6px;
  }

  @media (min-width: 992px) {
    #contents {
      height: 100vh;
      padding-bottom: 0;

      #payment-and-billing-details,
      #review-your-plan {
        padding: 60px 45px;
      }
    }

    #payment-and-billing-details {
      border-right: 1px solid #dedcde;
    }
  }

  .lh-normal {
    line-height: normal !important;
  }
}
</style>